/**
 * A equipe marcada na tabela, pronta para sair em papel ou em planilha.
 *
 * Papel: aqui ficam a ordem das pessoas exportadas (`ordenarPorNome`), a frase
 * da contagem (`contagemDeSelecionados`) e a planilha da equipe
 * (`planilhaDaEquipe`) — o que a barra de seleção, a `FolhaDaEquipe` e o botão
 * "⇩ Exportar Excel" repartem.
 *
 * Como usar: a folha e a planilha recebem a mesma lista, já passada por
 * `ordenarPorNome`, e por isso saem na mesma ordem. Ordenar só numa delas, ou
 * por outro critério, faria o papel e o arquivo exportados no mesmo clique
 * listarem as pessoas em ordens diferentes.
 *
 * A planilha tem as catorze colunas de `CABECALHOS_DA_EQUIPE`: as doze de
 * sempre e, no fim, Apelido e Departamento, que o cadastro guarda e a tabela
 * mostra — a planilha é a de todos os campos cadastrados, e não só dos que
 * cabem na tela. As quatro de benefício — ajuda de custo, ajuda médica,
 * alimentação e transporte — saem sempre vazias, porque o cadastro não guarda
 * esses campos. Elas continuam no arquivo para que Status e Observações
 * fiquem sempre na mesma coluna; tirá-las mudaria a posição das duas para quem
 * já lê o arquivo por coluna, e é pelo mesmo motivo que um campo novo deverá
 * entrar no fim, depois de Departamento.
 */
import { type Colaborador } from '@concierge/shared';

import { formatarCpf } from '@/shared/lib/formato';
import { type Planilha } from '@/shared/lib/planilha';
import { ROTULO_DO_STATUS } from './rotulos';

/**
 * A primeira linha da planilha, uma célula por coluna, na ordem de
 * `planilhaDaEquipe`.
 */
export const CABECALHOS_DA_EQUIPE = [
  'Nome',
  'Cargo',
  'CPF',
  'Residência',
  'Admissão',
  'Salário base',
  'Ajuda de custo',
  'Ajuda médica',
  'Alimentação',
  'Transporte',
  'Status',
  'Observações',
  'Apelido',
  'Departamento',
] as const;

/**
 * Põe as pessoas em ordem alfabética do nome completo.
 *
 * A comparação é a do português (`localeCompare` com `pt-BR`), que põe
 * "Álvaro" junto dos outros "A" em vez de depois do "Z". Devolve uma cópia: a
 * lista recebida, que costuma ser a da consulta, não muda de ordem.
 *
 * @param colaboradores - As pessoas marcadas, em qualquer ordem.
 * @returns Uma lista nova, do nome que vem primeiro ao que vem por último.
 */
export function ordenarPorNome(colaboradores: readonly Colaborador[]): Colaborador[] {
  return [...colaboradores].sort((uma, outra) =>
    uma.nomeCompleto.localeCompare(outra.nomeCompleto, 'pt-BR'),
  );
}

/**
 * A frase da contagem, com a concordância no singular e no plural.
 *
 * @param quantidade - Quantas pessoas estão marcadas; a barra só a pede com
 *   uma ou mais.
 * @returns `1 colaborador selecionado`, ou `N colaboradores selecionados`.
 */
export function contagemDeSelecionados(quantidade: number): string {
  return quantidade > 1
    ? `${String(quantidade)} colaboradores selecionados`
    : `${String(quantidade)} colaborador selecionado`;
}

/**
 * Monta a planilha da equipe marcada.
 *
 * O CPF sai pontuado e como texto, para o zero da frente não sumir; admissão
 * como data e salário como número, vazios para quem não os tem — a diarista,
 * por exemplo. A situação sai pelo `ROTULO_DO_STATUS`. Apelido e departamento
 * ausentes saem como célula vazia.
 *
 * @param colaboradores - As pessoas marcadas, já em `ordenarPorNome`; a
 *   planilha sai na ordem recebida.
 * @param hojeIso - A data de hoje em `AAAA-MM-DD`, como `hojeEmIso` a dá, que
 *   entra no nome do arquivo.
 * @returns A planilha, com a aba "Equipe ativa" e o arquivo
 *   `rh-domestico-equipe-selecionados-AAAA-MM-DD.xlsx`.
 */
export function planilhaDaEquipe(
  colaboradores: readonly Colaborador[],
  hojeIso: string,
): Planilha {
  return {
    nomeDoArquivo: `rh-domestico-equipe-selecionados-${hojeIso}.xlsx`,
    abas: [
      {
        nome: 'Equipe ativa',
        cabecalhos: CABECALHOS_DA_EQUIPE,
        linhas: colaboradores.map((colaborador) => [
          colaborador.nomeCompleto,
          colaborador.cargo,
          formatarCpf(colaborador.cpf),
          colaborador.residencia.apelido,
          colaborador.admissao === null ? null : { data: colaborador.admissao },
          colaborador.salario,
          null,
          null,
          null,
          null,
          ROTULO_DO_STATUS[colaborador.status],
          colaborador.observacao,
          colaborador.apelido,
          colaborador.departamento,
        ]),
      },
    ],
  };
}
